/**
 * P3-05: MidPanel —— 中栏 React 分区外壳（挂载于 #kgMidPanelViewport，
 * #gameContainerId 的兄弟节点，分区规则见 ./midPartition.ts 文件头）。
 *
 * 组成：TabNav（P3-03）+ 激活 tab 的已迁移内容组件（MIGRATED_TABS 注册表）。
 * 激活 tab 未迁移时渲染 null（React 分区由 syncMidPanelVisibility 隐藏，
 * dojo 中栏显示），迁移时渲染导航 + 内容（dojo 中栏隐藏）——两区域永不
 * 同时显示，导航条也不会双份。
 *
 * 显隐副作用：activeTabId 变化（每 tick 选择器驱动）→ syncMidPanelVisibility；
 * 卸载 → restoreMidPanelVisibility（dojo 中栏恢复可见）。
 *
 * 本阶段未启用（index.html 不调用 KGNewUI.mountMidPanel，注册表为空）；
 * 启用路径：首个 tab 迁移切换步骤（§3.1 起）登记 registerMigratedTab 并
 * 在 index.html initGame 调用 mountMidPanel。
 */
import { useEffect } from "react";
import type { GamePage } from "../bridge/GameBridge";
import { useGameTick } from "../bridge/useGameTick";
import TabNav from "./TabNav";
import {
  getMigratedTabComponent,
  isTabMigrated,
  restoreMidPanelVisibility,
  syncMidPanelVisibility,
} from "./midPartition";

export default function MidPanel() {
  const activeTabId = useGameTick(
    (game: GamePage | null) => (game?.ui?.activeTabId as string | undefined) ?? null,
  );

  const migrated = activeTabId !== null && isTabMigrated(activeTabId);

  useEffect(() => {
    syncMidPanelVisibility(activeTabId);
    return () => restoreMidPanelVisibility();
  }, [activeTabId]);

  if (!migrated || activeTabId === null) {
    return null;
  }

  const Content = getMigratedTabComponent(activeTabId);
  return (
    <>
      <TabNav />
      {/* Content 来自 MIGRATED_TABS 注册表（模块级稳定引用），非渲染期新建组件 */}
      {/* eslint-disable-next-line react-hooks/static-components */}
      {Content ? <Content /> : null}
    </>
  );
}
